import React from 'react'
import { Button, ButtonProps, Icon } from '@chakra-ui/react'
import {
    FaArrowRight,
    FaBell,
    FaEnvelope,
    FaImages,
    FaPaw,
    FaRuler,
    FaWandMagicSparkles,
} from 'react-icons/fa6'
import { navigate } from 'gatsby'

import { UserRoutes } from '~config/routes'
import { ClinicFieldsFragment } from '~graphql/generated/graphql'
import { useAskChckvetDock } from '~components/ask-chckvet/ask-chckvet-context'

import { TempAlert } from './types'

/** Stable read key for Ask Rocket. Renaming marks it unread again. */
export const ASK_ROCKET_ALERT_KEY = 'ask-rocket-launch'

// Example Alert
const exampleAlert: TempAlert = {
    title: 'Example Alert',
    description: 'This is an example alert',
    expiresAt: new Date('2026-06-01'),
    dismissedKey: 'example-alert',
    icon: FaBell,
    action: <TempAlertActionButton>Go to that</TempAlertActionButton>,
}

// Alerts
const automaticReschedulingAlert: TempAlert = {
    title: 'Automatic Rescheduling is Here!',
    description:
        'Allow clients to reschedule their appointments without requiring staff approval.',
    expiresAt: new Date('2026-03-01'),
    dismissedKey: 'automatic-rescheduling-alert',
    icon: FaBell,
    action: (
        <TempAlertActionButton
            onClick={() => navigate(UserRoutes.BookingSettings)}
        >
            Go to Booking Settings
        </TempAlertActionButton>
    ),
}

const simpleSchedulingRulesAlert: TempAlert = {
    title: 'Simpler Scheduling Rules!',
    description:
        'We recently added a new interface for managing basic scheduling rules to make them easier to manage and configure.',
    expiresAt: new Date('2026-05-01'),
    dismissedKey: 'simple-scheduling-rules-alert',
    icon: FaRuler,
    action: (
        <TempAlertActionButton
            onClick={() => navigate(UserRoutes.SchedulingRules)}
        >
            Go to Scheduling Rules
        </TempAlertActionButton>
    ),
    otherDisabledChecks: (
        clinic: Partial<ClinicFieldsFragment> | undefined,
    ) => {
        return !!clinic?.settings?.simple_scheduling_rules_enabled
    },
}

const postcardsAvailableAlert: TempAlert = {
    title: 'Postcards Are Now Fully Available!',
    description:
        'Get your clients back into your practice by utilizing THE highest-converting medium for client engagement. Reach out to support@chckvet.com to turn it on!',
    expiresAt: new Date('2026-07-01'),
    dismissedKey: 'postcards-available-alert',
    icon: FaEnvelope,
    action: (
        <a href="mailto:support@chckvet.com?subject=Postcards">
            <TempAlertActionButton>Contact Support</TempAlertActionButton>
        </a>
    ),
}

const portalCustomizationAlert: TempAlert = {
    title: 'New Portal Customizations',
    description:
        'Customize your client portal to be exactly how you want. Manage layout, custom links, and branding.',
    expiresAt: new Date('2026-05-15'),
    dismissedKey: 'portal-customization-alert',
    icon: FaPaw,
    action: (
        <TempAlertActionButton
            onClick={() => navigate(UserRoutes.PortalCustomization)}
        >
            Go to Portal Customization
        </TempAlertActionButton>
    ),
}

const patientPortalMarketingMaterialsAlert: TempAlert = {
    title: 'Patient Portal Marketing Materials',
    description:
        'Promote the Chckvet patient app with ready-made downloadable graphics and app store links.',
    expiresAt: new Date('2026-06-01'),
    dismissedKey: 'patient-portal-marketing-materials-alert',
    icon: FaImages,
    action: (
        <TempAlertActionButton
            onClick={() => navigate(UserRoutes.MarketingMaterials)}
        >
            Go to Marketing Materials
        </TempAlertActionButton>
    ),
}

const sharedMediaAlert: TempAlert = {
    title: 'Shared Media',
    description:
        'Upload, organize, and reuse images across your email templates. You can also share images with your clinic group.',
    expiresAt: new Date('2026-07-01'),
    dismissedKey: 'shared-media-alert',
    icon: FaImages,
    action: (
        <TempAlertActionButton onClick={() => navigate(UserRoutes.SharedMedia)}>
            Go to Shared Media
        </TempAlertActionButton>
    ),
}

const askRocketAlert: TempAlert = {
    title: 'Meet Ask Rocket',
    description:
        'Ask questions about any screen, client, or setting and get answers from your live clinic configuration. Open Ask Rocket and click Customize in the top-right of the panel to change its name, species, and colors.',
    // Unread through 60 days after the 2026-10-06 release, then it stays listed.
    expiresAt: new Date('2026-12-06T00:00:00'),
    dismissedKey: ASK_ROCKET_ALERT_KEY,
    icon: FaWandMagicSparkles,
    action: <OpenAskRocketButton />,
}

/**
 * Product announcements for the header What's New bell.
 * Add new alerts at the top. `expiresAt` is how long an alert counts as
 * unread; it stays in the list after that. Never rename `dismissedKey` —
 * that marks it unread again.
 */
export const TempAlerts: TempAlert[] = [
    askRocketAlert,
    patientPortalMarketingMaterialsAlert,
    portalCustomizationAlert,
    postcardsAvailableAlert,
    automaticReschedulingAlert,
    simpleSchedulingRulesAlert,
]

function OpenAskRocketButton() {
    const { open } = useAskChckvetDock()

    return (
        <TempAlertActionButton onClick={() => open()}>
            Open Ask Rocket
        </TempAlertActionButton>
    )
}

function TempAlertActionButton(props: ButtonProps) {
    return (
        <Button
            size="xs"
            variant="link"
            colorScheme="brand"
            rightIcon={<Icon as={FaArrowRight} boxSize={2.5} />}
            {...props}
        />
    )
}
